import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Sparkles, Target, Flame, TrendingUp, CheckCircle2, Pencil, Check, Angry, Frown, Meh, Smile, Laugh, BookOpen, Loader2 } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HabitCard from "@/components/HabitCard";
import HabitModal from "@/components/HabitModal";
import Celebration from "@/components/Celebration";
import WelcomeModal from "@/components/WelcomeModal";
import WeeklyTipModal from "@/components/WeeklyTipModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const QUOTES = [
  "Consistency beats intensity. One tick at a time.",
  "You do not rise to the level of your goals. You fall to the level of your systems.",
  "Never miss twice.",
  "Start ridiculously small. Aim for 80%, not perfect.",
];

const MOODS = [
  { v: 1, icon: Angry, label: "Rough" },
  { v: 2, icon: Frown, label: "Meh" },
  { v: 3, icon: Meh, label: "Okay" },
  { v: 4, icon: Smile, label: "Good" },
  { v: 5, icon: Laugh, label: "Great" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// ISO date (YYYY-MM-DD) of the Monday of the current week — the weekly-tip cadence key.
function mondayKey() {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}

// Stable week counter so the tip rotates a different one each week.
function weekIndex() {
  return Math.floor(new Date(mondayKey()).getTime() / (7 * 24 * 3600 * 1000));
}

function buildDays() {
  const days = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push(d);
  }
  return days;
}

export default function Dashboard() {
  const { user, refreshUser, setUser } = useAuth();
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedDay, setSelectedDay] = useState(todayISO());
  const [filter, setFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [celebrations, setCelebrations] = useState([]);
  const [focusEdit, setFocusEdit] = useState(false);
  const [focus, setFocus] = useState(user?.focus || "");
  const welcomeKey = user?.id ? `momentum_welcomed_${user.id}` : "momentum_welcomed";
  const [showWelcome, setShowWelcome] = useState(false);
  const weeklyKey = user?.id ? `momentum_weeklytip_${user.id}` : "momentum_weeklytip";
  const [showWeeklyTip, setShowWeeklyTip] = useState(false);

  useEffect(() => {
    if (user?.id && !localStorage.getItem(welcomeKey)) setShowWelcome(true);
  }, [user?.id, welcomeKey]);

  useEffect(() => {
    if (!user?.id) return;
    // Only after the user has been welcomed, and not while the welcome modal is open.
    if (localStorage.getItem(welcomeKey) && !showWelcome && localStorage.getItem(weeklyKey) !== mondayKey()) {
      setShowWeeklyTip(true);
    }
  }, [user?.id, welcomeKey, weeklyKey, showWelcome]);

  const dismissWeeklyTip = () => {
    localStorage.setItem(weeklyKey, mondayKey());
    setShowWeeklyTip(false);
  };

  const dismissWelcome = () => {
    localStorage.setItem(welcomeKey, "1");
    // Suppress the weekly tip for the user's FIRST week — it should only begin the week after the welcome.
    if (!localStorage.getItem(weeklyKey)) localStorage.setItem(weeklyKey, mondayKey());
    setShowWelcome(false);
  };

  const days = useMemo(buildDays, []);
  const quote = useMemo(() => QUOTES[new Date().getDate() % QUOTES.length], []);

  const load = async (day = selectedDay) => {
    try {
      const { data } = await api.get("/habits", { params: { day } });
      setHabits(data);
    } catch (e) {
      toast.error("Failed to load habits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(selectedDay); /* eslint-disable-next-line */ }, [selectedDay]);
  useEffect(() => { setFocus(user?.focus || ""); }, [user?.focus]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(habits.map((h) => h.category)))], [habits]);
  const filtered = filter === "All" ? habits : habits.filter((h) => h.category === filter);
  const doneCount = habits.filter((h) => h.completed).length;

  const fireConfetti = () => {
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ["#E9E604", "#C9C600", "#FF5722", "#F59E0B"] });
  };

  const handleGamification = (data, updated) => {
    if (data.xp_gained > 0) toast.success(`+${data.xp_gained} XP`, { duration: 1500 });
    const events = [];
    if (data.leveled_up) events.push({ type: "level", level: data.level });
    (data.new_achievements || []).forEach((a) => events.push({ type: "achievement", achievement: a }));
    if (events.length) setCelebrations((c) => [...c, ...events]);
    refreshUser();
    if (updated.length >= 2 && updated.every((h) => h.completed)) {
      setTimeout(fireConfetti, 150);
      toast.success("Perfect day — all habits done! 🔥");
    }
  };

  const toggle = async (habit) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/habits/${habit.id}/entry`, { date: selectedDay });
      const updated = habits.map((h) => h.id === habit.id ? { ...h, completed: data.completed, count: data.count, streak: data.streak } : h);
      setHabits(updated);
      handleGamification(data, updated);
    } catch (e) { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  const setCount = async (habit, count) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/habits/${habit.id}/entry`, { date: selectedDay, count });
      const updated = habits.map((h) => h.id === habit.id ? { ...h, completed: data.completed, count: data.count, streak: data.streak } : h);
      setHabits(updated);
      handleGamification(data, updated);
    } catch (e) { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  const saveHabit = async (form) => {
    try {
      if (editing) { await api.put(`/habits/${editing.id}`, form); toast.success("Habit updated"); }
      else { await api.post("/habits", form); toast.success("Habit created"); refreshUser(); }
      setModalOpen(false); setEditing(null); load(selectedDay);
    } catch (e) { toast.error("Save failed"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/habits/${deleteTarget.id}`);
      setHabits((hs) => hs.filter((h) => h.id !== deleteTarget.id));
      toast.success("Habit deleted");
    } catch (e) { toast.error("Delete failed"); }
    setDeleteTarget(null);
  };

  const saveFocus = async () => {
    try {
      await api.put("/me/focus", { focus });
      setUser((u) => ({ ...u, focus }));
      setFocusEdit(false);
      toast.success("Focus updated");
    } catch (e) { toast.error("Couldn't save focus"); }
  };

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (h) => { setEditing(h); setModalOpen(true); };

  return (
    <div className="space-y-8 pb-24 md:pb-8" data-testid="dashboard-page">
      <Celebration event={celebrations[0]} onClose={() => setCelebrations((c) => c.slice(1))} />
      <WelcomeModal
        open={showWelcome}
        onClose={dismissWelcome}
        onAddHabit={() => { dismissWelcome(); openNew(); }}
      />
      <WeeklyTipModal
        open={showWeeklyTip}
        onClose={dismissWeeklyTip}
        tipIndex={weekIndex()}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
            Hey {user?.name?.split(" ")[0] || "there"} 👋
          </h1>
          <p className="text-white mt-2 max-w-lg text-sm italic">"{quote}"</p>
        </div>
        <button
          data-testid="add-habit-button"
          onClick={openNew}
          className="h-12 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center gap-2 hover:opacity-90 transition-opacity shrink-0"
        >
          <Plus className="w-5 h-5" /> New Habit
        </button>
      </div>

      {/* My Focus */}
      <div className="p-4 rounded-2xl border border-[#242417] bg-[#12141C] flex items-center gap-3" data-testid="focus-card">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <Target className="w-5 h-5 text-emerald-400" />
        </div>
        {focusEdit ? (
          <div className="flex-1 flex gap-2">
            <input
              data-testid="focus-input"
              autoFocus
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveFocus()}
              placeholder="What's your focus this month? e.g. Move more, sleep better"
              className="flex-1 h-10 px-3 rounded-lg bg-[#0D0F17] border border-[#242417] outline-none focus:border-emerald-400 text-sm"
            />
            <button data-testid="focus-save-button" onClick={saveFocus} className="h-10 px-4 rounded-lg bg-emerald-500 text-black font-semibold text-sm">Save</button>
          </div>
        ) : (
          <button data-testid="focus-edit-button" onClick={() => setFocusEdit(true)} className="flex-1 text-left group">
            <span className="text-xs uppercase tracking-wider text-white">My focus</span>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{user?.focus || "Set your focus for the month"}</span>
              <Pencil className="w-3.5 h-3.5 text-white group-hover:text-emerald-400" />
            </div>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<CheckCircle2 className="w-5 h-5 text-emerald-400" />} label="Today's Progress" value={`${doneCount}/${habits.length}`} testid="stat-today" />
        <StatCard icon={<Target className="w-5 h-5 text-blue-400" />} label="Active Habits" value={habits.length} testid="stat-active" />
        <StatCard icon={<Flame className="w-5 h-5 text-orange-500" />} label="Best Streak" value={Math.max(0, ...habits.map((h) => h.streak || 0))} testid="stat-streak" />
        <StatCard icon={<TrendingUp className="w-5 h-5 text-purple-400" />} label="Completion" value={`${habits.length ? Math.round((doneCount / habits.length) * 100) : 0}%`} testid="stat-completion" />
      </div>

      {/* Day selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {days.map((d) => {
          const iso = d.toISOString().slice(0, 10);
          const active = iso === selectedDay;
          const isToday = iso === todayISO();
          return (
            <button
              key={iso}
              data-testid={`day-select-${iso}`}
              onClick={() => setSelectedDay(iso)}
              className={`flex flex-col items-center min-w-[3.5rem] py-2.5 rounded-xl border transition-colors ${
                active ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-400" : "border-[#242417] text-white hover:bg-white/5"
              }`}
            >
              <span className="text-xs uppercase">{d.toLocaleDateString("en-US", { weekday: "short" })}</span>
              <span className="font-mono-stat font-bold text-lg">{d.getDate()}</span>
              {isToday && <span className="w-1 h-1 rounded-full bg-emerald-400 mt-0.5" />}
            </button>
          );
        })}
      </div>

      {/* Category filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`category-filter-${c}`}
            onClick={() => setFilter(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === c ? "bg-white text-black border-white" : "border-[#242417] text-white hover:bg-white/5"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Habit grid */}
      {loading ? (
        <div className="text-white py-20 text-center">Loading habits…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#242417] rounded-2xl">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-emerald-400" />
          </div>
          <h3 className="font-display text-xl font-bold mb-1">No habits yet</h3>
          <p className="text-white mb-5">Create your first habit to start building momentum.</p>
          <button data-testid="empty-add-habit-button" onClick={openNew} className="h-11 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold inline-flex items-center gap-2">
            <Plus className="w-5 h-5" /> Add Habit
          </button>
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((h) => (
            <HabitCard key={h.id} habit={h} disabled={busy} onToggle={toggle} onSetCount={setCount} onEdit={openEdit} onDelete={setDeleteTarget} />
          ))}
        </motion.div>
      )}

      {/* Journal & weekly review */}
      <JournalWidget date={selectedDay} />

      <HabitModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSave={saveHabit} initial={editing} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#12141C] border-[#242417] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-white">This permanently removes the habit and all its tracked history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#33331f] text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function JournalWidget({ date }) {
  const [entry, setEntry] = useState({ mood: null, note: "", win: "", blocker: "", next_step: "" });
  const [showReview, setShowReview] = useState(false);
  const [savingDaily, setSavingDaily] = useState(false);
  const [savingWeekly, setSavingWeekly] = useState(false);

  useEffect(() => {
    api.get("/journal", { params: { date } }).then((r) => setEntry(r.data)).catch(() => {});
  }, [date]);

  const submit = async (kind) => {
    const setBusy = kind === "weekly" ? setSavingWeekly : setSavingDaily;
    setBusy(true);
    try {
      await api.post("/journal", { date, ...entry });
      toast.success(kind === "weekly" ? "Weekly review saved" : "Check-in saved");
    } catch (e) { toast.error("Couldn't save check-in"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 sm:p-6 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl" data-testid="journal-widget">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-emerald-400" />
        <h3 className="font-display text-lg font-semibold">Daily Check-in</h3>
      </div>

      <p className="text-xs uppercase tracking-wider text-white mb-2">How did today feel?</p>
      <div className="flex gap-2 mb-5">
        {MOODS.map((m) => (
          <button
            key={m.v}
            data-testid={`mood-${m.v}`}
            onClick={() => setEntry((x) => ({ ...x, mood: m.v }))}
            title={m.label}
            className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-colors ${
              entry.mood === m.v ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-400" : "border-[#242417] text-white hover:bg-white/5"
            }`}
          >
            <m.icon className="w-5 h-5" />
          </button>
        ))}
      </div>

      <textarea
        data-testid="journal-note-input"
        value={entry.note || ""}
        onChange={(e) => setEntry((x) => ({ ...x, note: e.target.value }))}
        placeholder="A quick note about your day…"
        rows={2}
        className="w-full p-3 rounded-xl bg-[#0D0F17] border border-[#242417] outline-none focus:border-emerald-400 text-sm resize-none"
      />

      <button
        data-testid="journal-submit-button"
        onClick={() => submit("daily")}
        disabled={savingDaily}
        className="mt-3 w-full h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
      >
        {savingDaily ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save check-in
      </button>

      <button data-testid="toggle-review-button" onClick={() => setShowReview((s) => !s)} className="mt-4 text-sm text-emerald-400 font-medium hover:underline">
        {showReview ? "Hide" : "Open"} Weekly Review →
      </button>

      {showReview && (
        <div className="mt-4" data-testid="weekly-review">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            ["win", "My win 🏆"],
            ["blocker", "What got in the way"],
            ["next_step", "Next time I will"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="block text-xs uppercase tracking-wider text-white mb-2">{label}</label>
              <textarea
                data-testid={`review-${key}`}
                value={entry[key] || ""}
                onChange={(e) => setEntry((x) => ({ ...x, [key]: e.target.value }))}
                rows={3}
                className="w-full p-3 rounded-xl bg-[#0D0F17] border border-[#242417] outline-none focus:border-emerald-400 text-sm resize-none"
              />
            </div>
          ))}
          </div>
          <button
            data-testid="weekly-review-submit-button"
            onClick={() => submit("weekly")}
            disabled={savingWeekly}
            className="mt-4 w-full sm:w-auto px-6 h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {savingWeekly ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Weekly Review
          </button>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon, label, value, testid }) => (
  <div data-testid={testid} className="p-5 rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">{icon}</div>
    </div>
    <div className="font-mono-stat text-2xl font-bold">{value}</div>
    <div className="text-xs text-white mt-1">{label}</div>
  </div>
);
